/**
 * Discipline Rating capture on the Resource Detail screen
 * (docs/specs/discipline-rating-fit-score.md Story E2). Each HELD Discipline gets
 * its own independent 1–5 rating — SA:5, DM:2 render as two rows, never a blend
 * (AC1) — with confirmed/unconfirmed state visible per row (AC2).
 *
 * RM assigns/modifies + confirms via `editResourceDisciplineRating`. There is no
 * SA self-submit path in this flow (D-0715-E) — non-RM viewers see a read-only
 * rating + confirmation badge, no controls.
 */

import { Check } from "lucide-react";
import { DisciplineChip } from "@/components/shared/discipline-chip";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type DisciplineRating,
  useAssignDisciplineRatings,
  useConfirmDisciplineRating,
  useDisciplineRatings,
} from "@/hooks/use-discipline-ratings";
import type { NymblRole } from "@/lib/auth";
import { useSession } from "@/lib/auth-client";
import { PERMISSION_MATRIX } from "@/lib/permissions";

function hasCapability(
  role: string | null | undefined,
  capability: keyof (typeof PERMISSION_MATRIX)[NymblRole],
): boolean {
  if (!role) return false;
  const matrix = PERMISSION_MATRIX[role as NymblRole];
  if (!matrix) return false;
  return matrix[capability] !== undefined;
}

/** Same 1–5 rating swatch convention as the Constraint Rating editor in this drawer. */
function ratingBadgeClass(rating: number | null): string {
  if (rating == null) return "bg-muted text-muted-foreground";
  if (rating >= 5) return "bg-success text-white";
  if (rating >= 4) return "bg-[var(--fit-strong)] text-white";
  if (rating >= 3) return "bg-[var(--fit-moderate)] text-white";
  return "bg-[var(--fit-at-risk)] text-white";
}

function DisciplineRatingRow({
  resourceId,
  entry,
  canEdit,
}: {
  resourceId: string;
  entry: DisciplineRating;
  canEdit: boolean;
}) {
  const assign = useAssignDisciplineRatings(resourceId);
  const confirm = useConfirmDisciplineRating(resourceId);
  const busy = assign.isPending || confirm.isPending;

  // Per-level help text authored in Configuration (discipline_rating_level), so hovering a
  // rating button — or the chip for the held rating — reveals what that level means.
  const helpByLevel = new Map(entry.levels.map((l) => [l.level, l.helpText]));
  const helpFor = (level: number | null): string | null =>
    level == null ? null : (helpByLevel.get(level) ?? null);

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-card p-2.5">
      <div className="flex min-w-0 items-center gap-2">
        <DisciplineChip
          code={entry.disciplineCode}
          title={entry.disciplineName}
          helpText={helpFor(entry.rating)}
        />
        <span
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded text-[11px] font-semibold ${ratingBadgeClass(entry.rating)}`}
          title={
            entry.rating == null
              ? `No Discipline Rating assigned yet for ${entry.disciplineName}`
              : `Discipline Rating ${entry.rating}/5 for ${entry.disciplineName}`
          }
        >
          {entry.rating ?? "—"}
        </span>
        <span
          className={`rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${
            entry.confirmed ? "bg-success-100 text-success-700" : "bg-warning-100 text-warning-700"
          }`}
          title={
            entry.confirmed
              ? `RM-confirmed${entry.confirmedAt ? ` · ${new Date(entry.confirmedAt).toLocaleDateString()}` : ""}`
              : "Unconfirmed — excluded from Fit Score until an RM confirms it"
          }
        >
          {entry.confirmed ? "Confirmed" : "Unconfirmed"}
        </span>
      </div>

      {canEdit && (
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <div
            className="inline-flex items-center gap-1"
            title="Assign / modify Discipline Rating (1–5)"
          >
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                disabled={busy}
                onClick={() => assign.mutate([{ disciplineId: entry.disciplineId, rating: n }])}
                aria-label={`Set ${entry.disciplineName} rating to ${n}`}
                aria-pressed={entry.rating === n}
                title={helpFor(n) ?? undefined}
                className={`flex h-6 w-6 items-center justify-center rounded text-[10px] font-semibold disabled:cursor-not-allowed disabled:opacity-40 ${
                  entry.rating === n
                    ? ratingBadgeClass(n)
                    : "border border-border bg-background text-muted-foreground hover:bg-muted"
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          <button
            type="button"
            disabled={busy || entry.rating == null || entry.confirmed}
            onClick={() => confirm.mutate(entry.disciplineId)}
            className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-1 text-[10px] font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
            title="RM confirms this rating before it can affect Fit Score"
          >
            <Check className="h-3 w-3" />
            Confirm
          </button>
        </div>
      )}
    </div>
  );
}

export function DisciplineRatingsSection({ resourceId }: { resourceId: string }) {
  const { data: session } = useSession();
  const canEdit = hasCapability(session?.user?.role, "editResourceDisciplineRating");
  const { data, isLoading, isError, error, refetch } = useDisciplineRatings(resourceId);

  if (isLoading) {
    return (
      <div className="space-y-1.5">
        <Skeleton className="h-11 w-full" />
        <Skeleton className="h-11 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load Discipline Ratings"}
        onRetry={() => refetch()}
      />
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border bg-card/50 px-3 py-4 text-center text-[11px] text-muted-foreground">
        No held Disciplines — grant a Discipline above to capture a rating.
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {data.map((entry) => (
        <DisciplineRatingRow
          key={entry.disciplineId}
          resourceId={resourceId}
          entry={entry}
          canEdit={canEdit}
        />
      ))}
    </div>
  );
}
